<template>
  <div ref="scrollContainer" class="flex-1 w-full overflow-y-auto deck-thumbnails-wrapper">
    <nav class="flex flex-col text-white bg-gray-800" data-testid="deck-sidebar" data-intercom-target="deck-slide-list">
      <div ref="slidesContainer" :class="['slides-container', { 'grid-view': isGridView }]">
        <ol ref="draggableContainer" class="flex mb-0 gap-4">
          <EditorDeckThumbnailSlideTile
            v-for="(slide, index) in modifiedSlides"
            :key="generateKey(slide)"
            :class="[
              'tile relative',
              {
                'first-of-grouped-model': !isGridView && isFirstOfGroupedModel(slide, index),
                'grouped-model': !isGridView && isGroupedModel(slide, index),
                'last-of-grouped-model': !isGridView && isLastOfGroupedModel(slide, index),
                'singular-model': !isGridView && isSingularModelSlide(slide, index),
              },
            ]"
            :should-show-model-id="!isGridView && (isFirstOfGroupedModel(slide, index) || isSingularModelSlide(slide, index))"
            :is-dragging="isDragging"
            :is-grid-view="isGridView"
            :slide="slide"
            :index="index"
            :order="slide.order"
            :selected="isSlideSelected(slide.id)"
            :data-slideid="slide.id"
            @click="handleSlideSelect"
            @ctrl-click:title="$emit('ctrl-click:title', $event)"
            @shift-click:title="$emit('shift-click:title', $event)"
            @contextmenu="openContextMenu($event, selectedSlides, slide)"
            @cancel-create-header-slide="$emit('cancel-create-header-slide')"
            @create-header-slide="$emit('create-header-slide', { ...$event, deck_id: deck.id })"
            @update:title="$emit('update:slide', { id: slide.id, title: $event })"
            @focus:title="setSortableDisabled(true)"
            @blur:title="$emit('blur:title'), setSortableDisabled(false)"
          />
        </ol>
      </div>
    </nav>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, type DeepReadonly, type PropType, type Ref } from 'vue'
import type { SiteSlide, Slide, SlideType } from '@/entities/deck'
import { debounce } from '@/lib/utils'
import type { DragStartEvent } from '@shopify/draggable'
import { MultiDragSortable } from '@/lib/editor/multi-drag-sortable'
import type { BookMeetingStatus } from '@/composables/useBookMeeting'
import { pluralize } from '~/lib/pluralize'

const props = defineProps({
  analyticsEventName: {
    type: String,
    required: true,
  },
  deck: {
    type: Object as PropType<EditDeck>,
    required: true,
  },
  fullSlideListCount: {
    type: Number,
    required: true,
  },
  isAddingSectionHeader: {
    type: Boolean,
    default: false,
  },
  isGridView: {
    type: Boolean,
    default: false,
  },
  requestSettings: {
    type: Object as PropType<DeepReadonly<BookMeetingStatus | null | undefined>>,
    default: () => ({}),
  },
  selectedSlideIds: {
    type: Array as PropType<number[]>,
    required: true,
  },
  slides: {
    type: Array as PropType<DeepReadonly<Slide[]>>,
    default: () => [],
  },
  uuid: {
    type: String,
    default: null,
  },
})

const emit = defineEmits([
  'click',
  'shift-click:title',
  'ctrl-click:title',
  'set-current-slide',
  'move-slide',
  'create-header-slide',
  'cancel-create-header-slide',
  'update:slide',
  'contextmenu',
  'duplicate-slides',
  'export-slides',
  'delete-slides',
  'replace-pdf-source',
  'copy-link-to-slide',
  'blur:title',
  'download-three-d-slide',
  'update-title-visibility',
  'bulk-replace-pdf-source',
  'multi-select',
  'reorder-slides',
])

const { $analytics } = useNuxtApp()

let sortable: MultiDragSortable
let isSortableDisabled = false
const { hasRole } = useUser()
const slidesContainer: Ref<HTMLElement | null> = ref(null)
const draggableContainer: Ref<HTMLElement | null> = ref(null)
const scrollContainer: Ref<HTMLElement | null> = ref(null)
const modifiedSlides: Ref<DeepReadonly<Slide[]>> = ref([])
const { slides, isAddingSectionHeader, selectedSlideIds } = toRefs(props)
const isSearching = computed(() => slides.value.length)
const shouldPreventScroll = ref(false)
const selectedSlides = computed(() => slides.value.filter(s => props.selectedSlideIds.includes(s.id)))
const orderedSelectedElements = computed(() => {
  return [...selectedSlides.value]
    .sort((a, b) => (a.order > b.order ? 1 : -1))
    .map(s => draggableContainer.value?.querySelector(`.tile[data-slideid="${s.id}"]`))
    .filter((x): x is Element => !!x)
})
const { vpeUrl } = useVpeUrl()

const isDragging = ref(false)

const toggleDragging = () => {
  isDragging.value = !isDragging.value
}

watch(
  () => props.isGridView,
  newValue => {
    if (!newValue) {
      _scrollSelectedSlideIntoView(selectedSlides.value[0]?.id)
    }
  }
)

onMounted(() => {
  // Note: this is required because of default browser behaviour trying to remember
  // scroll position (issue came about on Firefox Version 114.0.1 (64-bit))
  document.querySelector('.deck-thumbnails-wrapper')?.scrollTo(0, 0)
})

watch(draggableContainer, () => {
  if (draggableContainer.value) {
    sortable = new MultiDragSortable(draggableContainer.value, {
      draggable: '.tile',
      distance: 10,
      excludeHandle: '.ui-inline-editable',
      mirror: {
        constrainDimensions: true,
        appendTo: document.body,
        cursorOffsetX: -10,
        cursorOffsetY: 80,
      },
      scrollable: {
        speed: 16,
        sensitivity: 80,
        scrollableElements: [scrollContainer.value!],
      },
    })
    sortable.on('drag:start', toggleDragging)
    sortable.on('drag:stop', toggleDragging)
    sortable.on('sortable:stop', _reorder)
    sortable.setSelection(orderedSelectedElements.value)
  }
})

const scrollSlideIntoView = debounce((newIds: number[], oldIds: number[]) => {
  if (shouldPreventScroll.value) {
    shouldPreventScroll.value = false
    return
  }

  const hasAddedNewItem = newIds.length > oldIds.length && oldIds.every(id => newIds.includes(id))
  const hasRemovedItem = oldIds.length > newIds.length && newIds.every(id => oldIds.includes(id))

  if (!hasAddedNewItem && !hasRemovedItem) {
    _scrollSelectedSlideIntoView(newIds[0]!)
  }
}, 50)

watch(
  selectedSlideIds,
  (newIds, oldIds) => {
    scrollSlideIntoView(newIds, oldIds)
    sortable?.setSelection(orderedSelectedElements.value)
  },
  { flush: 'post' }
)

watch(
  [isAddingSectionHeader, isSearching],
  ([isAddingSectionHeader, isSearching]) => {
    if (isAddingSectionHeader) {
      const [_, filteredIndex] = slides.value.reduce(
        ([order, index], cur, curIndex) => {
          if (selectedSlideIds.value.includes(cur.id) && cur.order < order) {
            return [cur.order, curIndex]
          }
          return [order, index]
        },
        [Infinity, 0]
      )

      modifiedSlides.value = modifiedSlides.value
        .slice(0, filteredIndex)
        .concat(<DeepReadonly<Slide>>{ title: '', type: 'H', order: filteredIndex })
        .concat(modifiedSlides.value.slice(filteredIndex))

      nextTick(() => {
        document.querySelector<HTMLElement>('.new-section-header .editable')?.focus()
      })
      return
    }

    if (isSearching) {
      modifiedSlides.value = slides.value
    }
  },
  { immediate: true }
)

const cancelEvent = (e: DragStartEvent) => e.cancel()
watch(
  [slides, draggableContainer],
  ([s]) => {
    setSortableDisabled(s.length !== props.fullSlideListCount)
  },
  { immediate: true }
)

watch(slides, slides => (modifiedSlides.value = slides), { immediate: true })

function openContextMenu(e: PointerEvent, selectedSlides: DeepReadonly<Slide[]>, clickedSlide: DeepReadonly<Slide>) {
  const slides = selectedSlides.length === 1 || selectedSlides.every(s => s.id !== clickedSlide.id) ? [clickedSlide] : selectedSlides
  const isAllThreeDSlides = slides.every(slide => slide.type === 'M')
  const isPdfSlide = slides.length === 1 && slides[0].type === 'P'
  const isSiteSlide = slides.length === 1 && slides[0].type === 'S'
  const isSingleThreeDSlide = slides.length === 1 && slides[0].type === 'M'
  const isMultiplePdfSlides = slides.length > 1 && slides.every(slide => slide.type === 'P')
  const isNotHeaderSlide = slides.length === 1 && slides[0].type !== 'H'

  if (slides.length === 1) {
    emit('set-current-slide', slides[0].id)
    if (['M', 'S'].includes(slides[0].type) && !slides[0].camera_id) return // will show missing scene modal so no contextmenu
  }

  emit('contextmenu', {
    event: e,
    context: slides,
    options: [
      {
        label: 'Duplicate',
        call: () => emit('duplicate-slides', slides),
        testId: 'duplicate',
      },
      isAllThreeDSlides
        ? {
            label: `${slides.some(s => s.title_visible_in_3d_model) ? 'Hide' : 'Show'} slide
                  ${pluralize(slides.length, 'title', 'titles')}`,
            call: () => emit('update-title-visibility', slides),
            testId: 'hide-show',
          }
        : null,
      {
        label: 'Export to another Deck',
        call: () => emit('export-slides', slides),
        testId: 'export',
      },
      isNotHeaderSlide
        ? {
            label: 'Copy link to slide',
            call: () => emit('copy-link-to-slide', slides[0].id),
            testId: 'copy-link',
          }
        : null,
      isMultiplePdfSlides
        ? {
            label: 'Bulk replace PDF source',
            call: () => emit('bulk-replace-pdf-source', slides),
            testId: 'bulk-replace-pdf',
          }
        : null,
      isPdfSlide
        ? {
            label: 'Replace Slide',
            call: () => emit('replace-pdf-source', slides[0].id),
            testId: 'replace-pdf',
          }
        : null,
      isSiteSlide
        ? {
            label: 'Open Project in a new tab',
            call: () => {
              const siteSlide = slides[0] as SiteSlide
              window.open(`${vpeUrl}/projects/${siteSlide.project_id}/sites/${siteSlide.site_id}`, '_blank')
            },
            testId: 'open-360',
          }
        : null,
      isSingleThreeDSlide && hasRole('internal_admin')
        ? {
            label: 'Download as image',
            internalAdminOnly: true,
            subactions: {
              returnLabel: 'Back',
              actions: [
                {
                  label: 'Default size',
                  call: () => _emitDownloadImage(slides[0].id, null, null),
                  testId: 'download-image-default',
                },
                {
                  label: '1800 \xd7 800',
                  call: () => _emitDownloadImage(slides[0].id, 1800, 800),
                  testId: 'download-image-1800',
                },
                {
                  label: '900 \xd7 400',
                  call: () => _emitDownloadImage(slides[0].id, 900, 400),
                  testId: 'download-image-900',
                },
              ],
            },
            testId: 'download-image',
          }
        : null,
      {
        label: 'Multi-select by slide type',
        subactions: {
          returnLabel: 'Back',
          actions: [
            { label: 'Select all PDF Slides', call: () => _emitMultiSelect('P'), testId: 'select-pdfs' },
            { label: 'Select all 360 Slides', call: () => _emitMultiSelect('S'), testId: 'select-360s' },
            { label: 'Select all 3D Slides', call: () => _emitMultiSelect('M'), testId: 'select-3ds' },
          ],
        },
        testId: 'multi-select',
      },
      {
        label: 'Delete',
        call: () => emit('delete-slides', slides),
        testId: 'delete',
      },
    ]
      .filter(x => !!x)
      .map((obj, id) => ({ ...obj, id })),
  })
}

function _emitMultiSelect(type: SlideType) {
  shouldPreventScroll.value = true
  emit('multi-select', type)
}

function _emitDownloadImage(slideId: number, width: number | null, height: number | null) {
  emit('download-three-d-slide', { slideId, width, height })
}

function handleSlideSelect(e: MouseEvent & { payload: number }) {
  if (!e.payload) return

  const id = e.payload
  const index = slides.value.findIndex(s => id === s.id)

  if (e.metaKey || e.ctrlKey || e.shiftKey) {
    emit('click', Object.assign(e, { payload: id }))
    return
  }

  $analytics.track(`${props.analyticsEventName}SlideSelected`, {
    slideIndex: index,
    deckId: props.deck.id,
    deckName: props.deck.title,
    companyId: props.deck.company.id,
    companyName: props.deck.company.name,
    slideId: id,
    uuid: props.uuid,
  })

  emit('set-current-slide', id)
}

function isSlideSelected(id: number) {
  return selectedSlideIds.value.includes(id)
}

function setSortableDisabled(state: boolean) {
  // Don't want to set multiple cancelEvent listeners as we'll have to
  // remove all of them afterwards
  if (isSortableDisabled && state) return

  sortable?.[state ? 'on' : 'off']('drag:start', cancelEvent)
  isSortableDisabled = state
}

function generateKey(slide: DeepReadonly<Slide>): string {
  const requestSetting = `${slide.request_setting_id}-${
    slide.request_setting_id && props.requestSettings ? props.requestSettings.status : ''
  }`

  return `${slide.id} ${slide.title} ${requestSetting}`
}

function _reorder() {
  if (!draggableContainer.value) return

  const newOrder = sortable.getSortableElementsForContainer(draggableContainer.value).map((item, index) => ({
    id: parseInt((item as HTMLElement).dataset!.slideid ?? '', 10),
    order: index,
  }))

  emit('reorder-slides', newOrder)
}

function _scrollSelectedSlideIntoView(slideId: number) {
  if (!slidesContainer.value) return

  const idx = props.slides.findIndex(s => s.id === slideId)
  const selected = slidesContainer.value.querySelectorAll('.tile')[idx]

  if (!selected) return

  nextTick(() => {
    selected.scrollIntoView?.({
      block: 'nearest',
      behavior: 'smooth',
    })
  })
}

const isFirstOfGroupedModel = (slide: DeepReadonly<Slide>, index: number) =>
  slide.type === 'M' &&
  slide.model_id !== modifiedSlides.value[index - 1]?.model_id &&
  slide.model_id === modifiedSlides.value[index + 1]?.model_id
const isGroupedModel = (slide: DeepReadonly<Slide>, index: number) =>
  slide.type === 'M' &&
  (slide.model_id === modifiedSlides.value[index - 1]?.model_id || slide.model_id === modifiedSlides.value[index + 1]?.model_id)
const isLastOfGroupedModel = (slide: DeepReadonly<Slide>, index: number) =>
  slide.type === 'M' &&
  slide.model_id === modifiedSlides.value[index - 1]?.model_id &&
  slide.model_id !== modifiedSlides.value[index + 1]?.model_id
const isSingularModelSlide = (slide: DeepReadonly<Slide>, index: number) =>
  slide.type === 'M' &&
  slide.model_id !== modifiedSlides.value[index + 1]?.model_id &&
  slide.model_id !== modifiedSlides.value[index - 1]?.model_id
</script>

<style lang="stylus" scoped>
.slides-container
  -webkit-overflow-scrolling touch
  @apply p-4 relative

  ol
    @apply flex-col

  &.grid-view
    ol
      @apply grid gap-9 lg_py-16 lg_px-8 justify-center
      grid-template-columns: repeat(auto-fit, 240px);

    li
      width: 240px


  .tile
    scroll-margin: 8px

    &.grouped-model::before
      content: '';
      @apply overflow-visible bg-copper-200 h-full w-full block absolute opacity-20 pb-4 box-content z-0

    &.grouped-model.first-of-grouped-model::before
      @apply rounded-t-md

    &.grouped-model.last-of-grouped-model::before
      @apply rounded-b-md pb-0
</style>

<style lang="stylus">
// used by multidragsortable
.draggable--original
  @apply block

.tile-wrapper
  @apply flex

.tile-wrapper.draggable
  &-mirror
    @apply flex flex-col gap-4 text-white z-10 bg-gray-900 bg-opacity-70 rounded-md

  &-source--is-dragging
    @apply flex flex-col gap-4 bg-gray-800
</style>
